import { FeatureSwitchState } from './featureState';
import { isInsideRollout } from './rolloutBucket';
import { isRolloutState } from './rolloutState';
import { isStaffEmail } from './staffEmail';

/**
 * The grant that closes a feature for one account whatever else opens it.
 *
 * `ai-insights-block` and `cloudflare-beta-block` already follow this shape;
 * naming it here is what lets a block be honoured for every feature instead of
 * only the two that hand-rolled it.
 */
export function blockFeatureFor(flag: string): string {
  return `${flag}-block`;
}

export type ResolveFeatureInput = {
  readonly state: FeatureSwitchState;
  readonly email?: string | null;
  /**
   * The account's effective grants: the union of `users.available_features`
   * and the features its plan maps, which is what `/me` already sends the
   * dashboard. Passing only one half silently narrows the answer.
   */
  readonly features?: readonly string[] | null;
  /** The tenant account id (`users.id`), not the login id. */
  readonly accountId: number;
};

/**
 * Whether this account may use this feature.
 *
 * A block outranks `on` deliberately: pulling one account out of a fully
 * released feature is otherwise impossible without closing it for everyone.
 * Only `off` outranks a block, and it reaches staff too — a kill switch that
 * spares its authors is not one.
 *
 * `granted` has no staff bypass, which is where this parts company with
 * `canEnterCfBeta`. A bypass that reads well for one beta means every staff
 * account holding every feature in the switch row at once — `disable-clear-cache`
 * included. Staff-only is what `internal` is for.
 */
export function resolveFeature(flag: string, { state, email, features, accountId }: ResolveFeatureInput): boolean {
  if (state === 'off') {
    return false;
  }

  const granted = features ?? [];
  if (granted.includes(blockFeatureFor(flag))) {
    return false;
  }

  if (isRolloutState(state)) {
    return isStaffEmail(email) || granted.includes(flag) || isInsideRollout(flag, accountId, state.percent);
  }

  if (state === 'on') {
    return true;
  }

  if (state === 'internal') {
    return isStaffEmail(email);
  }

  return granted.includes(flag);
}

export function resolveEntitlement(flag: string, features: readonly string[] | null | undefined): boolean {
  const granted = features ?? [];
  return granted.includes(flag) && !granted.includes(blockFeatureFor(flag));
}
